import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Row,
  Col,
  Form,
  Input,
  DatePicker,
  Card,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Select
} from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { InvoiceService } from "../../../Services/Manage/InvoiceService";
import { UserService } from "../../../Services/Manage/UserService";

const ListAllInvoice = () => {
  const navigate = useNavigate();
  const [allInvoice, setAllInvoice] = useState([]);
  const [filteredInvoice, setFilteredInvoice] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [dateFilterType, setDateFilterType] = useState("invoice_date"); // 👈 Default filter type

  const getAllInvoive = async () => {
    try {
      setLoading(true);
      const user = await UserService.getOmeUser();
      const request = await InvoiceService.getAllInvoiceById(user?.uid);

      let invoicesData = [];
      if (Array.isArray(request.data)) {
        invoicesData = request.data.map(item => ({ ...item, key: item.id }));
      } else if (request.data && typeof request.data === 'object') {
        invoicesData = [{ ...request.data, key: request.data.id }];
      }

      setAllInvoice(invoicesData);
      setFilteredInvoice(invoicesData);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllInvoive();
  }, []);

  // ✅ Centralized filter function (search + date range)
  const applyAllFilters = (search = searchText, range = dateRange, type = dateFilterType) => {
    let filtered = [...allInvoice];

    // Search filter
    if (search) {
      const lower = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.invoice_no?.toLowerCase().includes(lower) ||
          item.customer_name?.toLowerCase().includes(lower) ||
          item.status?.toLowerCase().includes(lower)
      );
    }

    // Date filter
    if (range && range.length === 2) {
      const [start, end] = range;
      filtered = filtered.filter((item) => {
        const dateField = item[type];
        if (!dateField) return false;
        const d = dayjs(dateField);
        return d.isAfter(start.startOf("day")) && d.isBefore(end.endOf("day"));
      });
    }

    setFilteredInvoice(filtered);
  };

  // 🧱 Table Columns
  const columns = [
    {
      title: "S. No",
      key: "si_no",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Invoice No",
      dataIndex: "invoice_no",
      key: "invoice_no",
    },
    {
      title: "Quotation No",
      dataIndex: "qu_no",
      key: "qu_no",
    },
    {
      title: "Invoice Date",
      dataIndex: "invoice_date",
      key: "invoice_date",
      render: (date) => date ? new Date(date).toLocaleDateString() : "-",
    },
    {
      title: "Collected / Pending",
      key: "payment_summary",
      render: (_, record) => {
        const payments = record.payments || [];
        const totalCollected = payments.reduce(
          (sum, p) => sum + Number(p.paid_amt),
          0
        );
        const grandTotal = payments[0]?.grand_total
          ? Number(payments[0].grand_total)
          : 0;
        const pending = grandTotal - totalCollected;

        return (
          <>
            <div>Collected: <strong>{totalCollected}</strong></div>
            <div>Pending: <strong>{pending}</strong></div>
          </>
        );
      },
    },
    {
      title: "Payment Status",
      key: "payment_status",
      align: 'center',
      render: (_, record) => {
        const payments = record.payments || [];
        const isPaid = payments.some(p => p.status === "Paid");
        const status = isPaid ? "Paid" : "Pending";
        return <Tag style={{ fontSize: '16px', padding: '6px 12px' }} color={isPaid ? "green" : "orange"}>{status}</Tag>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      align: 'center',
      key: "status",
      render: (status) => (
        <Tag style={{ fontSize: '16px', padding: '6px 12px' }} color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: "Added By",
      dataIndex: "added_by",
      key: "added_by",
    },
    {
      title: "Added On",
      dataIndex: "added_on",
      key: "added_on",
      render: (date) => date ? new Date(date).toLocaleString() : "-",
    },
    {
      title: "Updated On",
      dataIndex: "updated_on",
      key: "updated_on",
      render: (date) => date ? new Date(date).toLocaleString() : "-",
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            onClick={() =>
              navigate('/invoice/view', { state: { id: record.qu_no } })
            }
            icon={<EyeOutlined />}
            size="large"
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  // Helper function for status tag colors
  const getStatusColor = (status) => {
    const statusColors = {
      'Created': 'blue',
      'Paid': 'green',
      'Pending': 'orange',
      'Cancelled': 'red',
      'Draft': 'gray',
    };
    return statusColors[status] || 'default';
  };

  return (
    <>
      <Card
        title={
          <Typography.Title level={4} style={{ margin: 0 }}>
            Manage Invoice
          </Typography.Title>
        }
        extra={
          <Button
            size='large'
            type="primary"
            onClick={() => navigate('/invoice/create')}
          >
            Create Invoice
          </Button>
        }
      >

        {/* ✅ Filters Section */}
        <Card style={{ marginBottom: 20 }}>
          <Row gutter={[16, 16]}>
            {/* Search Field */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item label="Search Invoice" layout="vertical">
                <Input
                  allowClear
                  placeholder="Search Invoice..."
                  size="large"
                  value={searchText}
                  onChange={(e) => {
                    const value = e.target.value.toLowerCase();
                    setSearchText(value);
                    applyAllFilters(value, dateRange, dateFilterType);
                  }}
                />
              </Form.Item>
            </Col>

            {/* Date Filter Type + Range */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item label="Date Filter" layout="vertical">
                <Space.Compact block>
                  <Select
                    size="large"
                    value={dateFilterType}
                    onChange={(val) => {
                      setDateFilterType(val);
                      applyAllFilters(searchText, dateRange, val);
                    }}
                    style={{ width: 180 }}
                    options={[
                      { label: "Invoice Date", value: "invoice_date" },
                      { label: "Added On", value: "added_on" },
                      { label: "Updated On", value: "updated_on" },
                    ]}
                  />
                  <DatePicker.RangePicker
                    style={{ width: "100%" }}
                    size="large"
                    value={dateRange}
                    onChange={(dates) => {
                      setDateRange(dates);
                      applyAllFilters(searchText, dates, dateFilterType);
                    }}
                    format="DD-MM-YYYY"
                  />
                </Space.Compact>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Table
          columns={columns}
          dataSource={filteredInvoice
            .slice()
            .sort((a, b) => new Date(b.updated_on) - new Date(a.updated_on))
          }
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </Card>
    </>
  )
}

export default ListAllInvoice;
